import type { AccountView, BillView, CardView, LedgerEntry, LimitsView, PendingLine, PendingView } from "../api/types.js";
import { calendarWeekday, clock, dateTime, formatMoney, lastDigits, signedMoney, statementDate, sumMoney } from "../lib/format.js";
import { limitsModel } from "./limits-model.js";
import type { IconId } from "../lib/icon.js";
import { cardDue, isDue } from "./card-due.js";

/**
 * 10 read-only answers (UX spec v3 §6.11, copy §8.2). The assistant's line has NO
 * figure in it (audited); every number sits on a bank card and is the bank's own:
 * `/v1/accounts`, `/v1/cards`, `/v1/spending` (ask b), the card-details reveal
 * (ask a). The only arithmetic is exact, in integer minor units, on the bank's own
 * strings: the total in accounts, and the spend's difference with the bank's own
 * prior period (dated as the bank dated it — the same number of days before, not
 * "last month").
 */
export interface ReadRow {
  readonly ic?: IconId;
  readonly initial?: string;
  readonly title: string;
  readonly sub?: string;
  readonly amount: string;
  /** Under the amount: what is available when a reservation holds part of it. */
  readonly small?: string;
}

export interface ReadAnswer {
  readonly say: string;
  readonly lead: string;
  readonly asof?: string;
  readonly stat?: { readonly figure: string; readonly sub: string };
  readonly rows: readonly ReadRow[];
  readonly total?: { readonly label: string; readonly amount: string };
  readonly kv?: readonly { readonly k: string; readonly v: string }[];
  /** The assistant's chip under the card (bill due): it starts a draft — the full path, with a signature. */
  readonly pay?: { readonly label: string; readonly small: string; readonly words: string };
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ICON: Readonly<Record<string, IconId>> = { savings: "i-bank", joint_savings: "i-users", investment_settlement: "i-trend" };
/** Home's order (home-model.ts), so the two screens list the accounts alike. */
const RANK: Readonly<Record<string, number>> = { savings: 0, current: 1, joint_savings: 2, investment_settlement: 3 };

function minor(v: string): bigint {
  return BigInt(v.replace(".", ""));
}

function decimal(n: bigint): string {
  const neg = n < 0n;
  const d = (neg ? -n : n).toString().padStart(3, "0");
  return `${d.slice(0, -2)}.${d.slice(-2)}`;
}

/**
 * `at`: the bank's `as_of` on the read (DCTA-85 P1-2) — none, and the card prints no time. `accountId` (DCTA-203): the one
 * account the bank's answer names — that row alone, no total; one the read does not hold, or none named: every account.
 */
export function balanceAnswer(all: readonly AccountView[], at: string | undefined, accountId?: string): ReadAnswer {
  const named = accountId === undefined ? [] : all.filter((a) => a.account_id === accountId);
  const accounts = named.length > 0 ? named : all;
  const sgd = named.length > 0 ? [] : accounts.filter((a) => a.currency === "SGD");
  return {
    say: "Here's what you have.",
    lead: "Accounts",
    ...(at === undefined ? {} : { asof: `read at ${clock(at)}` }),
    rows: [...accounts].sort((x, y) => (RANK[x.type] ?? 9) - (RANK[y.type] ?? 9)).map((a) => ({
      ic: ICON[a.type] ?? "i-bank",
      title: a.display_name,
      sub: lastDigits(a.masked_number),
      amount: formatMoney(a.balance, a.currency),
      ...(a.available_balance === a.balance ? {} : { small: `${formatMoney(a.available_balance, a.currency)} available` }),
    })),
    ...(sgd.length === 0 ? {} : { total: { label: "Total in accounts", amount: formatMoney(sumMoney(sgd.map((a) => a.balance)), "SGD") } }),
  };
}

export interface SpendingReport {
  readonly currency: string;
  readonly period: { readonly label: string; readonly from: string; readonly to: string };
  readonly prior_period: { readonly from: string; readonly to: string };
  /** What the period is compared with (DCTA-77, P2-10); an older bank sends none. */
  readonly compare_to?: "prior_period" | "same_dates_last_month";
  readonly total: string;
  readonly count: number;
  readonly prior_total: string;
  readonly categories: readonly {
    readonly category: string;
    readonly total: string;
    readonly count: number;
    readonly prior_total: string;
    readonly top_counterparties: readonly { readonly name: string; readonly total: string; readonly count: number }[];
  }[];
}

/** A category key as words ("card_purchases" → "Card purchases"): the bank names the key, not the label. */
export function categoryLabel(key: string): string {
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const payments = (n: number): string => `${String(n)} ${n === 1 ? "payment" : "payments"}`;

/** "1–24 Sep" from the bank's own period (calendar dates, Singapore); one day is that day ("12 Oct", DCTA-206). */
function span(from: string, to: string): string {
  const a = statementDate(`${from}T04:00:00Z`);
  const b = statementDate(`${to}T04:00:00Z`);
  if (from === to) return a;
  const [da, ma] = a.split(" ");
  const [db, mb] = b.split(" ");
  return ma === mb ? `${da ?? ""}–${db ?? ""} ${mb ?? ""}` : `${a} – ${b}`;
}

/**
 * DCTA-206 (A2): several categories read as one — the labels joined ("Dining and groceries"), their sum, their count, the
 * same sum before, and a row for each one the read holds (never one it does not).
 */
function spendOf(r: SpendingReport, keys: readonly string[]): { label: string; total: string; count: number; prior: string; rows: SpendingReport["categories"] } {
  const held = r.categories.filter((c) => keys.includes(c.category)).sort((a, b) => keys.indexOf(a.category) - keys.indexOf(b.category));
  const labels = keys.map((k, i) => (i === 0 ? categoryLabel(k) : categoryLabel(k).toLowerCase()));
  const label = labels.length > 1 ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1] ?? ""}` : (labels[0] ?? "Spending");
  return { label, total: sumMoney(held.map((c) => c.total)), count: held.reduce((n, c) => n + c.count, 0), prior: sumMoney(held.map((c) => c.prior_total)), rows: held };
}

export function spendAnswer(r: SpendingReport, category: string | readonly string[] | undefined): ReadAnswer {
  const month = MONTHS[Number(r.period.from.slice(5, 7)) - 1] ?? "";
  const when = r.period.label === "this_month" ? `${month} so far` : span(r.period.from, r.period.to);
  const one = typeof category === "string" ? category : undefined;
  const many = typeof category === "object" ? spendOf(r, category) : undefined;
  const all = category === undefined;
  const cat = one === undefined ? undefined : r.categories.find((c) => c.category === one);
  const total = many?.total ?? cat?.total ?? (all ? r.total : "0.00");
  const count = many?.count ?? cat?.count ?? (all ? r.count : 0);
  const prior = many?.prior ?? cat?.prior_total ?? (all ? r.prior_total : "0.00");
  const diff = minor(total) - minor(prior);
  const than = span(r.prior_period.from, r.prior_period.to);
  const compare = diff === 0n ? `the same as ${than}` : `${formatMoney(decimal(diff), r.currency)} ${diff > 0n ? "more" : "less"} than ${than}`;
  const label = many?.label ?? (one === undefined ? "Spending" : categoryLabel(one));
  const categoryRow = (c: SpendingReport["categories"][number]) => ({ initial: categoryLabel(c.category).charAt(0), title: categoryLabel(c.category), sub: payments(c.count), amount: formatMoney(c.total, r.currency) });
  return {
    say: `${all ? "Your spending" : label}, ${when}.`,
    lead: label,
    asof: span(r.period.from, r.period.to),
    // P2-10: the compare only against the same dates last month — a rolling window of the
    // same length ("more than 8–31 Aug") reads as a mistake, so it is not printed.
    stat: { figure: formatMoney(total, r.currency), sub: r.compare_to === "same_dates_last_month" ? `${payments(count)} · ${compare}` : payments(count) },
    rows:
      one === undefined
        ? (many?.rows ?? r.categories).map(categoryRow)
        : (cat?.top_counterparties ?? []).map((p) => ({
            initial: (/[\p{L}\p{N}]/u.exec(p.name)?.[0] ?? "·").toUpperCase(),
            title: p.name,
            sub: payments(p.count),
            amount: formatMoney(p.total, r.currency),
          })),
  };
}

function cardName(c: CardView): string {
  return `${c.display_name ?? (c.type === "credit" ? "Credit card" : "Debit card")} ${lastDigits(c.masked_pan)}`;
}

/**
 * "When is my card bill due?" — the statement from the card read, and the assistant's offer to pay it. DCTA-131: once
 * anything has been paid toward the statement, the figure is what is still due on it (`cardDue`, the bank's
 * `statement_due_now`) under DCTA-132's name for it, "Still due on the statement" (§F.2 row 15: one name per figure);
 * the statement's own balance is named beside it, the minimum is shown only while some of it is still due, and the
 * offer pays what is still due — none at all when nothing is (it would pay the statement twice).
 */
export function billAnswer(c: CardView): ReadAnswer | undefined {
  // DCTA-208: the card is the customer's (one with a statement, or their pick) — never the first credit card found.
  if (c.type !== "credit" || c.statement_balance === undefined) return undefined;
  const currency = c.currency ?? "SGD";
  const due = cardDue(c);
  const left = due.statement ?? c.statement_balance;
  const figure = formatMoney(left, currency);
  const by = c.due_date === undefined ? "" : ` · due ${calendarWeekday(c.due_date)}`;
  // Ruling Q9 (DCTA-132, Umakhan): one name for what a card owes now — "Outstanding balance".
  const owed = c.current_balance === undefined ? [] : [{ k: "Outstanding balance", v: formatMoney(c.current_balance, currency) }];
  const offer = { label: "Pay the statement", small: `${figure} · you check it, then sign`, words: "Pay my credit card bill" };
  if (!due.paid) {
    return {
      say: "Your card bill.",
      lead: cardName(c),
      stat: { figure, sub: `Statement balance${by}` },
      rows: [],
      kv: [...(due.minimum === undefined ? [] : [{ k: "Minimum", v: formatMoney(due.minimum, currency) }]), ...owed],
      pay: offer,
    };
  }
  const open = isDue(left);
  // F16 (ask tt, §F.7 DCTA-139): the statement paid and the card still owes — the offer pays the outstanding balance
  // ("outstanding balance" is what the bank's card position reads as owed now). Nothing owed: no offer.
  const outstanding =
    !open && c.current_balance !== undefined && isDue(c.current_balance)
      ? { label: "Pay the outstanding balance", small: `${formatMoney(c.current_balance, currency)} · you check it, then sign`, words: "Pay the outstanding balance on my credit card" }
      : undefined;
  return {
    say: "Your card bill.",
    lead: cardName(c),
    stat: { figure, sub: `Still due on the statement${open && c.due_date !== undefined ? ` · by ${calendarWeekday(c.due_date)}` : ""}` },
    rows: [],
    kv: [
      { k: "Statement balance", v: formatMoney(c.statement_balance, currency) },
      ...(isDue(due.minimum) ? [{ k: "Minimum", v: formatMoney(due.minimum as string, currency) }] : []),
      ...owed,
    ],
    ...(open ? { pay: offer } : outstanding !== undefined ? { pay: outstanding } : {}),
  };
}

export interface CardAnswer {
  readonly cardId: string;
  readonly lead: string;
  readonly masked: { readonly number: string; readonly expiry: string; readonly cvv: string };
}

/**
 * The card whose details to show: the one the bank names (DCTA-202: `executed[].card_id`, the customer's pick from "Which
 * card?") or the one card that fits the app's own words (DCTA-208, `card-choice.ts`) — or nothing when the read does not
 * hold it, never another card in its place. No card of the app's own choosing (it was the credit card first).
 */
export function cardAnswer(cards: readonly CardView[], cardId: string): CardAnswer | undefined {
  const c = cards.find((x) => x.card_id === cardId);
  return c === undefined ? undefined : cardAnswerFor(c);
}

/** One card's details block, masked until the customer's tap (15 Cards' Show details, DCTA-111). */
export function cardAnswerFor(c: CardView): CardAnswer {
  return { cardId: c.card_id, lead: cardName(c), masked: { number: c.masked_pan, expiry: "••/••", cvv: "•••" } };
}

const operations = (n: number): string => `${String(n)} ${n === 1 ? "operation" : "operations"}`;

/**
 * DCTA-203 — "How much do I owe on my bills?" as the bank drafts it: its outstanding bills (`GET /v1/bills`, what is still
 * owed from the book), soonest due first, each named as the bank names it (DCTA-204 R5: the registry's name and the
 * masked account's digits, on the bill). A bill with nothing owed (paid, 0.00) is not "what's due" — the bank's own
 * "Nothing is due." counts amounts above zero. A bill the bank names no biller for is "Bill" — never an id. A bill's
 * reference is anyone's text: it is never printed on a read.
 */
export function billsAnswer(all: readonly BillView[]): ReadAnswer {
  const bills = all.filter((b) => minor(b.amount.value) > 0n);
  const sorted = [...bills].sort((a, b) => a.due_date.localeCompare(b.due_date) || a.bill_id.localeCompare(b.bill_id));
  const rows = sorted.map((b): ReadRow => {
    const digits = b.masked_account === undefined ? "" : ` ${lastDigits(b.masked_account)}`;
    return {
      ic: "i-receipt",
      title: b.biller_name === undefined ? "Bill" : `${b.biller_name}${digits}`,
      sub: `Due ${calendarWeekday(b.due_date)}`,
      amount: formatMoney(b.amount.value, b.amount.currency),
    };
  });
  const currency = bills[0]?.amount.currency;
  const same = currency !== undefined && bills.every((b) => b.amount.currency === currency);
  return {
    say: bills.length === 0 ? "Nothing is due." : "Here's what's due.",
    lead: "Bills",
    rows,
    ...(same && bills.length > 1 ? { total: { label: "Total due", amount: formatMoney(sumMoney(bills.map((b) => b.amount.value)), currency) } } : {}),
  };
}

/** DCTA-203 — "What are my limits?" as the bank drafts it: the Limits page's own rows (limits-model.ts), as the bank reads them. */
export function limitsAnswer(read: LimitsView, nowMs: number): ReadAnswer {
  const kv = limitsModel(read, nowMs).sections.flatMap((s) => s.rows.map((r) => ({ k: r.label, v: r.value.map((p) => (typeof p === "string" ? p : p.money)).join("") })));
  return { say: "Here are your limits.", lead: "Limits", rows: [], kv };
}

/**
 * DCTA-203 — "Show me my recent transactions" as the bank drafts it: the latest lines of its ledger read (`GET
 * /v1/ledger/entries`, newest first), five at most: who, the day, how much in or out. A line's reference is anyone's text
 * (an injection seed sits in one): it is never printed on a read; Activity has the statement.
 */
export function recentAnswer(entries: readonly LedgerEntry[]): ReadAnswer {
  const rows = [...entries]
    .sort((a, b) => b.posted_at.localeCompare(a.posted_at))
    .slice(0, 5)
    .map((e): ReadRow => ({
      initial: (/[\p{L}\p{N}]/u.exec(e.counterparty_name ?? "")?.[0] ?? "·").toUpperCase(),
      title: e.counterparty_name ?? (e.direction === "credit" ? "Money in" : "Money out"),
      sub: statementDate(e.posted_at),
      amount: signedMoney(e.amount, e.currency, e.direction),
    }));
  return { say: rows.length === 0 ? "No recent activity." : "Here's your recent activity.", lead: "Recent activity", rows };
}

const WAITING: Readonly<Record<string, string>> = {
  awaiting_signature: "Waiting for your signature",
  awaiting_step_up: "Waiting for your security token",
  signing: "Being signed",
  signed: "Signed, about to go",
  held: "Held",
  partially_executed: "Held",
};

/**
 * DCTA-203 — "What payments are waiting?" as the bank drafts it (`GET /v1/gateway/pending`). DCTA-204 (R6): only what the
 * bank says is still `waiting` (never `!expired`: a held payment's signing window ended long ago and it still waits), one
 * row per line it lists — who or what it goes to and how much, under what it waits for and, held, when it goes out. An
 * entry with no lines (an older bank) is its own row, by what it waits for.
 */
export function pendingAnswer(pending: readonly PendingView[]): ReadAnswer {
  const rows = pending
    .filter((p) => p.waiting ?? !p.expired)
    .flatMap((p): ReadRow[] => {
      const why = WAITING[p.state] ?? "Waiting";
      const when = (hold: string | undefined): string => (hold === undefined ? "" : ` · goes out ${dateTime(hold)}`);
      const lines = p.lines ?? [];
      if (lines.length === 0) return [{ ic: "i-clock", title: why, sub: `${operations(p.steps)}${when(p.hold_until)}`, amount: "" }];
      return lines.map((l) => ({
        ic: "i-clock",
        title: lineTitle(l) ?? why,
        sub: `${why}${when(l.hold_until ?? p.hold_until)}`,
        amount: l.amount === undefined ? "" : `${l.amount_is_max === true ? "up to " : ""}${formatMoney(l.amount.value, l.amount.currency)}`,
      }));
    });
  return { say: rows.length === 0 ? "Nothing is waiting." : "Here's what's waiting.", lead: "Waiting", rows };
}

/** A waiting line titled as its card row is (describe.ts): an order by its side, a loosening by what it does, else who. */
function lineTitle(l: PendingLine): string | undefined {
  const to = l.to;
  if (to === undefined) return undefined;
  if (l.type === "securities_order") return `${l.side === "sell" ? "Sell" : "Buy"} ${to.name}`;
  if (l.type === "protective_action") {
    if (l.action !== "raise_limit") return `${l.action === "unfreeze_card" ? "Unfreeze" : "Unlock"} ${to.name}`;
    return `Raise the daily limit${l.new_limit === undefined ? "" : ` to ${formatMoney(l.new_limit.value, l.new_limit.currency)}`}`;
  }
  return `${to.name}${to.masked === undefined ? "" : ` ${lastDigits(to.masked)}`}`;
}
